import { useState, useEffect, useCallback } from "react";
import { Shield, Clock, FileText, Users, Trash2, Download, Loader2, RefreshCw, AlertTriangle, ChevronDown, ChevronUp, Archive } from "lucide-react";
import { Button } from "../ui/button";
import { toast } from "sonner";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ACTION_LABELS = {
  login: "Login",
  login_failed: "Login Failed",
  logout: "Logout",
  mfa_setup: "MFA Setup",
  mfa_verified: "MFA Verified",
  mfa_failed: "MFA Failed",
  lap_report_created: "LAP Created",
  lap_report_viewed: "LAP Viewed",
  lap_status_updated: "LAP Updated",
  lap_report_deleted: "LAP Deleted",
  lap_reports_exported: "LAP Exported",
  data_exported: "Data Exported",
  user_created: "User Created",
  settings_changed: "Settings Changed",
  session_expired: "Session Expired",
  session_forced_logout: "Forced Logout",
};

const ACTION_COLORS = {
  login: "bg-green-100 text-green-800",
  login_failed: "bg-red-100 text-red-800",
  mfa_failed: "bg-red-100 text-red-800",
  lap_report_deleted: "bg-red-100 text-red-800",
  session_forced_logout: "bg-orange-100 text-orange-800",
  lap_report_created: "bg-blue-100 text-blue-800",
  lap_reports_exported: "bg-purple-100 text-purple-800",
  data_exported: "bg-purple-100 text-purple-800",
};

export const SecurityPanel = ({ getAuthHeader }) => {
  const [tab, setTab] = useState("audit");
  const [logs, setLogs] = useState([]);
  const [sessions, setSessions] = useState([]);
  const [retention, setRetention] = useState(null);
  const [loading, setLoading] = useState(true);
  const [logFilter, setLogFilter] = useState("");
  const [logLimit, setLogLimit] = useState(50);

  const fetchData = useCallback(async () => {
    try {
      const params = new URLSearchParams({ limit: logLimit });
      if (logFilter) params.append("action", logFilter);
      const [logsRes, sessionsRes, retentionRes] = await Promise.all([
        axios.get(`${API}/admin/audit-logs?${params}`, getAuthHeader()),
        axios.get(`${API}/admin/sessions`, getAuthHeader()),
        axios.get(`${API}/admin/data-retention/status`, getAuthHeader()),
      ]);
      setLogs(logsRes.data);
      setSessions(sessionsRes.data);
      setRetention(retentionRes.data);
    } catch {
      toast.error("Failed to load security data");
    } finally {
      setLoading(false);
    }
  }, [getAuthHeader, logFilter, logLimit]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const forceLogout = async (sessionId) => {
    try {
      await axios.delete(`${API}/admin/sessions/${sessionId}`, getAuthHeader());
      toast.success("Session terminated");
      fetchData();
    } catch {
      toast.error("Failed to terminate session");
    }
  };

  const archiveReports = async () => {
    if (!window.confirm("Archive all LAP reports older than 2 years?")) return;
    try {
      const res = await axios.post(`${API}/admin/data-retention/archive`, {}, getAuthHeader());
      toast.success(`Archived ${res.data.archived_count} reports`);
      fetchData();
    } catch {
      toast.error("Failed to archive");
    }
  };

  const purgeReports = async () => {
    if (!window.confirm("PERMANENTLY DELETE all LAP reports older than 7 years? This cannot be undone.")) return;
    try {
      const res = await axios.post(`${API}/admin/data-retention/purge`, {}, getAuthHeader());
      toast.success(`Purged ${res.data.purged_count} reports`);
      fetchData();
    } catch {
      toast.error("Failed to purge");
    }
  };

  const exportLogs = async () => {
    try {
      const res = await axios.get(`${API}/admin/audit-logs/export`, getAuthHeader());
      const blob = new Blob([JSON.stringify(res.data, null, 2)], { type: "application/json" });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `audit_logs_${new Date().toISOString().slice(0, 10)}.json`;
      a.click();
      toast.success(`Exported ${res.data.count} log entries`);
    } catch {
      toast.error("Export failed");
    }
  };

  if (loading) {
    return <div className="flex items-center justify-center py-16"><Loader2 className="w-6 h-6 animate-spin text-warm-600" /></div>;
  }

  return (
    <div className="space-y-6" data-testid="security-panel">
      {/* Overview Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <div className="bg-white border border-gray-100 rounded-xl p-4 text-center">
          <p className="text-2xl font-bold text-gray-900">{logs.length}</p>
          <p className="text-xs text-gray-500">Audit Events</p>
        </div>
        <div className="bg-white border border-gray-100 rounded-xl p-4 text-center">
          <p className="text-2xl font-bold text-gray-900">{sessions.length}</p>
          <p className="text-xs text-gray-500">Active Sessions</p>
        </div>
        <div className="bg-white border border-gray-100 rounded-xl p-4 text-center">
          <p className="text-2xl font-bold text-green-700">{retention?.active || 0}</p>
          <p className="text-xs text-gray-500">Active Records</p>
        </div>
        <div className="bg-white border border-gray-100 rounded-xl p-4 text-center">
          <p className="text-2xl font-bold text-orange-700">{retention?.eligible_for_deletion || 0}</p>
          <p className="text-xs text-gray-500">Pending Deletion</p>
        </div>
      </div>

      {/* Tab Nav */}
      <div className="flex gap-2">
        {[
          { id: "audit", label: "Audit Log", icon: FileText },
          { id: "sessions", label: "Sessions", icon: Users },
          { id: "retention", label: "Data Retention", icon: Archive },
        ].map((t) => (
          <Button key={t.id} size="sm" variant={tab === t.id ? "default" : "outline"}
            className={tab === t.id ? "bg-warm-700 text-white" : ""} onClick={() => setTab(t.id)}>
            <t.icon className="w-4 h-4 mr-1" /> {t.label}
          </Button>
        ))}
        <Button size="sm" variant="ghost" onClick={fetchData} className="ml-auto">
          <RefreshCw className="w-4 h-4" />
        </Button>
      </div>

      {/* Audit Log Tab */}
      {tab === "audit" && (
        <div className="space-y-3">
          <div className="flex gap-2 items-center">
            <select className="border border-gray-200 rounded-lg px-3 py-2 text-sm" value={logFilter}
              onChange={(e) => setLogFilter(e.target.value)} data-testid="audit-filter">
              <option value="">All Actions</option>
              {Object.entries(ACTION_LABELS).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
            </select>
            <select className="border border-gray-200 rounded-lg px-3 py-2 text-sm" value={logLimit}
              onChange={(e) => setLogLimit(Number(e.target.value))}>
              <option value={50}>Last 50</option>
              <option value={100}>Last 100</option>
              <option value={500}>Last 500</option>
            </select>
            <Button size="sm" variant="outline" onClick={exportLogs} className="ml-auto gap-1" data-testid="export-audit-btn">
              <Download className="w-4 h-4" /> Export JSON
            </Button>
          </div>

          {logs.length === 0 ? (
            <div className="text-center py-12 text-gray-500">
              <Shield className="w-10 h-10 mx-auto mb-3 text-gray-300" />
              <p>No audit events recorded yet</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead><tr className="border-b text-left text-xs text-gray-500">
                  <th className="py-2 px-3">Time</th>
                  <th className="py-2 px-3">Action</th>
                  <th className="py-2 px-3">Resource</th>
                  <th className="py-2 px-3">User</th>
                  <th className="py-2 px-3">IP</th>
                  <th className="py-2 px-3">Details</th>
                </tr></thead>
                <tbody>
                  {logs.map((log, i) => (
                    <tr key={i} className="border-b border-gray-50 hover:bg-gray-50">
                      <td className="py-2 px-3 text-xs text-gray-400 whitespace-nowrap">
                        {new Date(log.timestamp).toLocaleString()}
                      </td>
                      <td className="py-2 px-3">
                        <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${ACTION_COLORS[log.action] || "bg-gray-100 text-gray-700"}`}>
                          {ACTION_LABELS[log.action] || log.action}
                        </span>
                      </td>
                      <td className="py-2 px-3 text-xs text-gray-600">{log.resource}</td>
                      <td className="py-2 px-3 text-xs text-gray-600">{log.user_id?.slice(0, 12) || "system"}</td>
                      <td className="py-2 px-3 text-xs text-gray-400">{log.ip_address || "N/A"}</td>
                      <td className="py-2 px-3 text-xs text-gray-500 max-w-[200px] truncate">{log.details}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {/* Sessions Tab */}
      {tab === "sessions" && (
        <div className="space-y-3">
          {sessions.length === 0 ? (
            <div className="text-center py-12 text-gray-500">
              <Users className="w-10 h-10 mx-auto mb-3 text-gray-300" />
              <p>No active partner sessions</p>
            </div>
          ) : (
            <div className="space-y-2">
              {sessions.map((s) => (
                <div key={s.session_id} className="border border-gray-200 rounded-lg p-4 flex items-center justify-between">
                  <div>
                    <p className="font-medium text-sm text-gray-900">{s.user_email || s.user_id}</p>
                    <p className="text-xs text-gray-500">
                      Last active: {new Date(s.last_activity).toLocaleString()} | IP: {s.ip_address || "N/A"}
                    </p>
                  </div>
                  <Button size="sm" variant="outline" className="text-red-600 border-red-200 hover:bg-red-50"
                    onClick={() => forceLogout(s.session_id)}>
                    <AlertTriangle className="w-4 h-4 mr-1" /> Force Logout
                  </Button>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Data Retention Tab */}
      {tab === "retention" && retention && (
        <div className="space-y-6">
          <div className="bg-warm-50 border border-warm-200 rounded-xl p-6">
            <h3 className="font-semibold text-gray-900 mb-4" style={{ fontFamily: 'Cormorant Garamond, Georgia, serif' }}>
              LAP Data Retention Policy
            </h3>
            <div className="grid grid-cols-3 gap-4 text-center">
              <div className="bg-white rounded-lg p-4 border border-green-200">
                <p className="text-2xl font-bold text-green-700">{retention.active}</p>
                <p className="text-xs text-gray-600 mt-1">Active (0 to 2 years)</p>
                <p className="text-xs text-gray-400">Full access</p>
              </div>
              <div className="bg-white rounded-lg p-4 border border-yellow-200">
                <p className="text-2xl font-bold text-yellow-700">{retention.archived}</p>
                <p className="text-xs text-gray-600 mt-1">Archived (2 to 7 years)</p>
                <p className="text-xs text-gray-400">Restricted access</p>
              </div>
              <div className="bg-white rounded-lg p-4 border border-red-200">
                <p className="text-2xl font-bold text-red-700">{retention.eligible_for_deletion}</p>
                <p className="text-xs text-gray-600 mt-1">Eligible for Deletion (7+ years)</p>
                <p className="text-xs text-gray-400">Awaiting purge</p>
              </div>
            </div>
          </div>

          <div className="flex gap-3">
            <Button onClick={archiveReports} className="bg-yellow-600 hover:bg-yellow-700 text-white gap-1">
              <Archive className="w-4 h-4" /> Archive Reports (2+ years)
            </Button>
            <Button onClick={purgeReports} variant="outline" className="text-red-600 border-red-200 hover:bg-red-50 gap-1">
              <Trash2 className="w-4 h-4" /> Purge Expired (7+ years)
            </Button>
          </div>

          <div className="bg-gray-50 rounded-xl p-4 text-sm text-gray-600">
            <p className="font-medium text-gray-900 mb-2">Retention Policy:</p>
            <ul className="space-y-1">
              <li>Active (0 to 2 years): Records available for ongoing case management and service delivery.</li>
              <li>Archived (2 to 7 years): Records moved to restricted access for grant compliance and legal requirements.</li>
              <li>Deleted (7+ years): Records permanently and irreversibly removed from the database.</li>
            </ul>
          </div>
        </div>
      )}
    </div>
  );
};
